import Button from "@/ui/components/Button"
import { CurrencyPicker } from "@/components/common/element/fields/CurrencyPicker"
import { CommonInput } from "@/components/common/element/fields/CommonInput"
import * as TrinityAPI from "@/requests/TrinityAPI"

import type { IUser } from "@/types/user/IUser"
import { useEffect, useMemo, useState, type FC } from "react"

import style from "../style.module.scss"
import { ModalPopUp } from "@/components/common/element/ModalPopUp"
import { IModalMessage } from "@/types/error/IModalMessage"
import { spacedNum } from "@/components/common/CommonHelper"
import Dropdown from "@/ui/components/Dropdown"
import { IExchangeDto } from "@/types/exchange/IExchangeDto"

type Props = {
  user: IUser | null
  onClose: () => void
  refetch: () => void
}

export const CurrencyExchangeForm: FC<Props> = ({ user, onClose, refetch }) => {
  const [modalMessage, setModalMessage] = useState<IModalMessage | null>(null)
  const [formData, setFormData] = useState({
    fromAmount: "",
    rate: "",
    fromCurrency: "RUB",
    toCurrency: "USD"
  })
  const [toAmount, setToAmount] = useState<string>("")
  const [userBalances, setUserBalances] = useState<Record<string, number> | null>(null)
  const [signOverride, setSignOverride] = useState<"*" | "/">("*")
  const [isExchangeLoading, setExchangeLoading] = useState(false)

  const isExchangeDisabled = useMemo(() => {
    return (
      !formData.fromAmount ||
      !formData.rate ||
      !formData.fromCurrency ||
      !formData.toCurrency ||
      !toAmount ||
      !user?.userId
    )
  }, [formData, toAmount, user?.userId])

  const handleChangeFromAmount = (value?: string) => {
    setFormData({
      ...formData,
      fromAmount: value || ""
    })
  }

  const handleChangeRate = (value?: string) => {
    setFormData({
      ...formData,
      rate: value || ""
    })
  }

  const handleChangeFromCurrency = (value: string) => {
    setFormData({
      ...formData,
      fromCurrency: value
    })
  }

  const handleChangeToCurrency = (value: string) => {
    setFormData({
      ...formData,
      toCurrency: value
    })
  }

  const handleExchange = () => {
    setExchangeLoading(true)

    const fromAmount = Number(formData.fromAmount?.toString().replaceAll(" ", "")) * 100

    const payload: IExchangeDto = {
      userId: user?.userId || 0,
      amount: fromAmount.toString(),
      from: formData.fromCurrency,
      to: formData.toCurrency,
      exchangeRate: formData.rate?.toString().replaceAll(" ", ""),
      signOverride
    }

    TrinityAPI.exchangeUserBalance(
      payload,
      response => {
        if (response.status === 200) {
          window.location.reload()
        } else {
          setModalMessage({ title: "Ошибка!", text: "Ошибка обмена: " + response.data.message })
        }
      },
      () => setExchangeLoading(false)
    )
  }

  useEffect(() => {
    if (formData.fromAmount && formData.rate && formData.fromCurrency && formData.toCurrency) {
      const fromAmount = Number(formData.fromAmount?.toString().replaceAll(" ", "")) * 100

      const payload: IExchangeDto = {
        amount: fromAmount.toString(),
        from: formData.fromCurrency,
        to: formData.toCurrency,
        exchangeRate: formData.rate?.toString().replaceAll(" ", ""),
        signOverride
      }

      TrinityAPI.calculateExchangeRate(payload, response => {
        if (response.status === 200) {
          setToAmount(response.data.amount)
        } else {
          setToAmount("0")
        }
      })
    }

    if (!formData.rate) {
      setToAmount("0")
    }
  }, [formData.fromCurrency, formData.toCurrency, formData.fromAmount, formData.rate, signOverride])

  useEffect(() => {
    if (user?.userId) {
      TrinityAPI.getAdminUserBalances(user.userId, response => {
        if (response.status === 200) {
          setUserBalances(response.data)
        } else {
          setModalMessage({ title: "Ошибка!", text: "Ошибка загрузки балансов пользователя: " + response.data.message })
        }
      })
    }
  }, [user?.userId])

  if (!user) {
    return null
  }

  return (
    <>
      <ModalPopUp message={modalMessage} close={() => setModalMessage(null)} error={undefined} />
      <section className="popup-forms index-top" id="error-popup">
        <div className="container-popup-forms">
          <button className="close-btn-card" id="password-close" onClick={onClose} />
          <div className="popup-card">
            <h4 className="heading-chapter">Обмен валют для юзера {user.username}</h4>
            <div>
              <div className={style.formTitle}>Текущие балансы:</div>
              <div className={style.formBalance}>
                {!!userBalances &&
                  Object.keys(userBalances).map(el => (
                    <div>
                      {el}: {spacedNum(((userBalances[el] || 0) / 100).toFixed(2))}
                    </div>
                  ))}
              </div>
            </div>
            <div className={style.formContent}>
              <div>
                <CommonInput
                  typeName={"number"}
                  label={"Сумма к обмену"}
                  placeholder={"Введите сумму к обмену"}
                  name={"rateBefore"}
                  value={formData.fromAmount}
                  errorModel={undefined}
                  _callback={v => handleChangeFromAmount(v)}
                />
                <CurrencyPicker
                  label={"Валюта"}
                  name={`contractorCurrency`}
                  errorModel={undefined}
                  initValue={formData.fromCurrency}
                  _callback={handleChangeFromCurrency}
                  clazzName={"fieldset-200"}
                />
                <div>
                  Баланс {formData.fromCurrency}:{" "}
                  {userBalances?.[formData.fromCurrency]
                    ? spacedNum(((userBalances?.[formData.fromCurrency] || 0) / 100).toFixed(2))
                    : "0"}
                </div>
              </div>
              <div>{">"}</div>
              <div>
                <CommonInput
                  typeName={"number"}
                  label={`Сумма в ${formData.toCurrency}`}
                  placeholder={"Получит в итоге"}
                  name={"toAmount"}
                  value={toAmount ? spacedNum(((Number(toAmount) || 0) / 100).toFixed(2)) : "0"}
                  errorModel={undefined}
                  readonly
                  _callback={() => {}}
                />
                <CurrencyPicker
                  label={"Валюта"}
                  name={`contractorCurrency`}
                  errorModel={undefined}
                  initValue={formData.toCurrency}
                  _callback={handleChangeToCurrency}
                  clazzName={"fieldset-200"}
                />
                <div>
                  Баланс {formData.toCurrency}:{" "}
                  {userBalances?.[formData.toCurrency]
                    ? spacedNum(((userBalances?.[formData.toCurrency] || 0) / 100).toFixed(2))
                    : "0"}
                </div>
              </div>
            </div>

            <div className={style.exchangeRate}>
              <CommonInput
                typeName={"number"}
                label={`Стоимость ${formData.toCurrency} за 1 ${formData.fromCurrency}`}
                placeholder={"Введите курс для расчета"}
                name={"rate"}
                value={formData.rate}
                errorModel={undefined}
                _callback={v => handleChangeRate(v)}
              />
              <Dropdown
                label="Знак операции"
                value={{
                  value: signOverride,
                  label: signOverride
                }}
                onChange={option => setSignOverride(option?.value as "*" | "/")}
                options={[
                  {
                    value: "*",
                    label: "*"
                  },
                  {
                    value: "/",
                    label: "/"
                  }
                ]}
              />
            </div>

            <div className="flex-row-24">
              <Button disabled={isExchangeDisabled} loading={isExchangeLoading} onClick={handleExchange}>
                Обменять
              </Button>
              <Button variant="grey" onClick={onClose}>
                Закрыть
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
